import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
import { getDay, format, startOfDay, isEqual } from 'date-fns';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import DateUtils from '../../utils/date/DateUtils';
var CLOSED_LABEL = 'Closed';
var OPEN_ALL_DAY_LABEL = 'Open 24 Hours';
var MOMENT_SUNDAY = 0;
var FISHERMAN_SUNDAY = 7;
export var NO_LABEL = 'NO_LABEL';
export var HOURS_LABELS = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};
var DAY_LABELS = new Set(Object.values(HOURS_LABELS).map(function (l) {
  return l.toLowerCase();
}));
export function getHourLabel(hour) {
  // Returns empty string if the label is a day label. Otherwise, returns hour.label
  var _hour$label = hour.label,
    label = _hour$label === void 0 ? '' : _hour$label;
  var hourLabel = label.toLowerCase();
  return DAY_LABELS.has(hourLabel) ? '' : label;
}
function getFormattedTimeMoment(timeMoment) {
  if (isEqual(timeMoment, startOfDay(timeMoment))) {
    return 'Midnight';
  }
  return format(timeMoment, 'h:mma');
}
function isClosed(open, close) {
  // TODO: change name or fix because this is not closed
  return !isNumber(open) || !isNumber(close);
}
function isOpenAllDay(hours, day) {
  // Get all opening hours for day
  var hoursForDay = hours.reduce(function (acc, opHour) {
    if (opHour.day === day - 1) {
      if (opHour.close <= opHour.open) {
        // it closes after midnight
        return [].concat(_toConsumableArray(acc), [{
          open: 0,
          close: opHour.close
        }]);
      }
    }
    if (opHour.day === day) {
      return [].concat(_toConsumableArray(acc), [{
        open: opHour.open,
        close: opHour.close
      }]);
    }
    return _toConsumableArray(acc);
  }, []);

  // Ensure all hours are sorted by open key
  hoursForDay.sort(function (a, b) {
    return a.open - b.open;
  });

  // Check that hours for day covers the whole day without any gap
  if (hoursForDay.length === 1) {
    return hoursForDay[0].open === 0 && (hoursForDay[0].close === 0 || hoursForDay[0].close === 2400);
  }
  var openAllDay = hoursForDay[0].open === 0;
  for (var i = 1; i < hoursForDay.length; i += 1) {
    // Check that every split hour is attached to the next
    openAllDay = openAllDay && hoursForDay[i - 1].close >= hoursForDay[i].open;
    if (i === hoursForDay.length - 1) {
      // Check that the last hour closes at midnight or after it
      openAllDay = openAllDay && (hoursForDay[i].close <= hoursForDay[i].open || hoursForDay[i].close === 0 || hoursForDay[0].close === 2400);
    }
  }
  return openAllDay;
}
function createTimeValue(hours, hourDay) {
  var open = hourDay.open,
    close = hourDay.close,
    day = hourDay.day;
  if (isClosed(open, close)) return CLOSED_LABEL;
  if (isOpenAllDay(hours, day)) return OPEN_ALL_DAY_LABEL;
  var openTime = DateUtils.parseHourInt(open);
  var openString = getFormattedTimeMoment(openTime);
  var closeTime = DateUtils.parseHourInt(close);
  var closeString = getFormattedTimeMoment(closeTime);
  return "".concat(openString, " - ").concat(closeString);
}
function convertMomentDay(momentDay) {
  var todayInt = getDay(momentDay);
  return todayInt === MOMENT_SUNDAY ? FISHERMAN_SUNDAY : todayInt;
}
export function setStartDayInGroup(hourGroups, dayInt) {
  var firstTodayIdx = hourGroups.findIndex(function (hourGroup) {
    return hourGroup.startDay <= dayInt && dayInt <= hourGroup.endDay;
  });
  if (firstTodayIdx >= 0) {
    return [].concat(_toConsumableArray(hourGroups.slice(firstTodayIdx)), _toConsumableArray(hourGroups.slice(0, firstTodayIdx)));
  }
  return hourGroups;
}
export function setFirstHoursInGroup(hourGroups, timezone) {
  var today = DateUtils.getTimezoneAwareNow(timezone);
  var todayInt = convertMomentDay(today);
  return setStartDayInGroup(hourGroups, todayInt);
}
export function getHourGroups(hourGroups, timezone, startWithToday, startDay) {
  var todayFirstInHoursGroup = setFirstHoursInGroup(hourGroups, timezone);
  var finalHourGroups = startWithToday ? todayFirstInHoursGroup : setStartDayInGroup(hourGroups, startDay);
  return [todayFirstInHoursGroup, finalHourGroups];
}

/**
 * Groups hours by day. Each object in the array will have a hoursValue array
 * containing all hour strings for the same day.
 *
 * Returns an array of objects with keys:
 * - dayLabel: str
 * - hoursValue: [str]
 * - hours: [{open, close, day, label}]
 * - startDay: int
 * - endDay: int
 */
export function groupByDay(hours, getLabel) {
  var hoursMap = {};
  hours.forEach(function (hour) {
    var day = hour.day,
      open = hour.open,
      close = hour.close;
    var key = day;
    if (!hoursMap[key]) {
      hoursMap[key] = {
        dayLabel: getLabel(day),
        hoursValue: [],
        hours: [],
        startDay: day,
        endDay: day
      };
    }
    hoursMap[key] = _objectSpread(_objectSpread({}, hoursMap[key]), {}, {
      hoursValue: [].concat(_toConsumableArray(hoursMap[key].hoursValue), [createTimeValue(hours, {
        open: open,
        close: close,
        day: day
      })]),
      hours: [].concat(_toConsumableArray(hoursMap[key].hours), [hour])
    });
  });
  return Object.values(hoursMap);
}
function hourStr(_ref) {
  var open = _ref.open,
    close = _ref.close,
    label = _ref.label;
  return "".concat(open, " - ").concat(close, " - ").concat(label);
}

/**
 * Groups consecutive days that have the same hour string.
 *
 * Returns an array of objects with keys:
 * - dayLabel: str
 * - hoursValue: [str]
 * - hours: [{open, close, day, label}]
 * - startDay: int
 * - endDay: int
 */
export function groupByDayAndTime(hours, getLabel) {
  var hoursGroupedByDay = groupByDay(hours, getLabel);

  // Group consecutive days with the same list of hourString
  // Create a list of objects, where each object has 3 values: hoursValue, start day, end day
  var orderedDays = hoursGroupedByDay.sort(function (aDay, bDay) {
    return aDay.startDay - bDay.startDay;
  });
  var groups = [];
  var startDayForGroup = orderedDays[0];
  var previousDay = orderedDays[0];
  var currentDayHourStr = null;
  var previousDayHourStr = null;
  orderedDays.forEach(function (currentDay, currentIndex) {
    if (currentIndex !== 0) {
      // The idea is to compare day string by day string and while they
      // are the same they will be grouped together. Every group is split
      // when a day string is different to the previous one or some edge cases
      // when we are checking the last day of the week
      previousDay = orderedDays[currentIndex - 1];
      currentDayHourStr = currentDay.hours.map(function (hour) {
        return hourStr(hour);
      }).join(',');
      previousDayHourStr = previousDay.hours.map(function (hour) {
        return hourStr(hour);
      }).join(',');
      if ((currentDayHourStr !== previousDayHourStr || previousDay.startDay + 1 !== currentDay.startDay) && currentIndex !== orderedDays.length - 1) {
        groups.push({
          hoursValue: previousDay.hoursValue,
          hours: previousDay.hours,
          startDay: startDayForGroup.startDay,
          endDay: previousDay.endDay
        });
        startDayForGroup = currentDay;
      } else if (currentDayHourStr === previousDayHourStr && currentIndex === orderedDays.length - 1) {
        groups.push({
          hoursValue: previousDay.hoursValue,
          hours: previousDay.hours,
          startDay: startDayForGroup.startDay,
          endDay: currentDay.endDay
        });
      } else if ((currentDayHourStr !== previousDayHourStr || previousDay.startDay + 1 !== currentDay.startDay) && currentIndex === orderedDays.length - 1) {
        groups.push({
          hoursValue: previousDay.hoursValue,
          hours: previousDay.hours,
          startDay: startDayForGroup.startDay,
          endDay: previousDay.endDay
        });
        groups.push({
          hoursValue: currentDay.hoursValue,
          hours: currentDay.hours,
          startDay: currentDay.startDay,
          endDay: currentDay.endDay
        });
      }
    }
    if (orderedDays.length === 1) {
      groups.push({
        hoursValue: currentDay.hoursValue,
        hours: currentDay.hours,
        startDay: currentDay.startDay,
        endDay: currentDay.startDay
      });
    }
  });
  return groups.map(function (_ref2) {
    var hoursValue = _ref2.hoursValue,
      allHours = _ref2.hours,
      startDay = _ref2.startDay,
      endDay = _ref2.endDay;
    var hoursGroupLabel;
    if (startDay === endDay) {
      hoursGroupLabel = getLabel(startDay);
    } else {
      hoursGroupLabel = getLabel(startDay, endDay);
    }
    return {
      dayLabel: hoursGroupLabel,
      hoursValue: hoursValue,
      hours: allHours,
      startDay: startDay,
      endDay: endDay
    };
  });
}

/**
 * Groups hours in a two-level hierarchy.
 * First hierarchy is the label (i.e: lunch, breakfast, takeaway)
 * Second hierarchy is the dayLabel (label that comes from groupByDay or
 * groupByDayAndTime)
 *
 * Returns an array of arrays. Each subarray has 2 values. The first is the
 * main label and the second is an array of objects:
 * [
 *   [mainLabel, [{
 *     - secondaryLabel: str
 *     - hoursValue: [str]
 *     - hours: [{open, close, day, label}]
 *     - startDay: int
 *     - endDay: int
 *   }]
 * ]
 *
 */
export function groupByLabel(hours, getLabel, displayOption, startWithToday, timezone, dayToStartWith) {
  // First create a map with labels as keys and array of hours as value
  var labelToHours = {};
  hours.forEach(function (hour) {
    var lbl = getHourLabel(hour) || NO_LABEL;
    if (!labelToHours[lbl]) {
      labelToHours[lbl] = [];
    }
    labelToHours[lbl] = [].concat(_toConsumableArray(labelToHours[lbl]), [hour]);
  });

  // Finally, create a new map with labels as keys and a groupByDay or
  // groupByDayAndTime according to displayOption
  var labelToGroup = [];

  // leave label NO_LABEL first in the array
  var labels = Object.keys(labelToHours).sort(function (lbl1, lbl2) {
    if (lbl1 === NO_LABEL) return -1;
    if (lbl2 === NO_LABEL) return 1;
    return 0;
  });
  var todaysHourOfOperation = null;
  labels.forEach(function (label) {
    var groupedHourGroups = displayOption === 'grouped' ? groupByDayAndTime(labelToHours[label], getLabel) : groupByDay(labelToHours[label], getLabel);
    var _getHourGroups = getHourGroups(groupedHourGroups, timezone, startWithToday, dayToStartWith),
      _getHourGroups2 = _slicedToArray(_getHourGroups, 2),
      todayFirstInHoursGroup = _getHourGroups2[0],
      hourGroups = _getHourGroups2[1];
    if (!todaysHourOfOperation) {
      var _todayFirstInHoursGro = _slicedToArray(todayFirstInHoursGroup, 1);
      todaysHourOfOperation = _todayFirstInHoursGro[0];
      todaysHourOfOperation.mainLabel = label;
    }
    var finalHourGroups = hourGroups.map(function (_ref3) {
      var hoursValue = _ref3.hoursValue,
        groupHours = _ref3.hours,
        startDay = _ref3.startDay,
        endDay = _ref3.endDay,
        dayLabel = _ref3.dayLabel;
      return {
        hoursValue: hoursValue,
        hours: groupHours,
        startDay: startDay,
        endDay: endDay,
        secondaryLabel: dayLabel
      };
    });
    labelToGroup.push([label, finalHourGroups]);
  });
  return [labelToGroup, todaysHourOfOperation];
}

/**
 * Groups hours in a two-level hierarchy.
 * First hierarchy is the dayLabel (i.e: lunch, breakfast, takeaway)
 * Second hierarchy is the dayLabel (label that comes from groupByDay or
 * groupByDayAndTime)
 *
 * Returns an object with labels as key and each value is an array of
 * objects:
 * [
 *   [mainLabel, [{
 *     - secondaryLabel: str
 *     - hoursValue: [str]
 *     - hours: [{open, close, day, label}]
 *     - startDay: int
 *     - endDay: int
 *   }]
 * ]
 *
 */
export function groupByDayLabel(hours, getLabel, displayOption, startWithToday, timezone, startDay) {
  var groupedHourGroups = displayOption === 'grouped' ? groupByDayAndTime(hours, getLabel) : groupByDay(hours, getLabel);
  var _getHourGroups3 = getHourGroups(groupedHourGroups, timezone, startWithToday, startDay),
    _getHourGroups4 = _slicedToArray(_getHourGroups3, 2),
    todayFirstInHoursGroup = _getHourGroups4[0],
    hourGroups = _getHourGroups4[1];
  var todaysHourOfOperation = todayFirstInHoursGroup[0];

  // Set dayLabel as the main label and for each hour in the group, there will
  // be an object with the hour label as the dayLabel, to share the same
  // format that the other way of grouping
  var grouping = [];
  hourGroups.forEach(function (group) {
    var dayLabel = group.dayLabel;
    var groupHours = group.hours.map(function (hour, idx) {
      return {
        secondaryLabel: getHourLabel(hour),
        hoursValue: [group.hoursValue[idx]],
        hours: [hour],
        startDay: hour.day,
        endDay: hour.day
      };
    });
    grouping.push([dayLabel, groupHours]);
  });
  return [grouping, todaysHourOfOperation];
}
export function allDayHoursAreUndefined(hours) {
  return (hours || []).every(function (_ref4) {
    var open = _ref4.open,
      close = _ref4.close;
    return isNil(open) || isNil(close);
  });
}